/**
 * The Lifeline day track (fix-78).
 *
 * Every mark comes from the desk: the board's window (`settings.lifeline_days`),
 * the rescue rule's first day, the lifecycle rows' first days, and the
 * extension's length (`settings.trial_extension_days`). The console holds no
 * day of its own — a copy here is how "Day 3" stayed on the board after the
 * rescue rule moved to day 2.
 */
export interface TrackStep {
  template_id: number;
  title: string;
  day_from: number | null;
  day_to: number | null;
  closes: boolean;
}

export interface TrackSpec {
  window_days: number;
  rescue_from_day: number;
  extension_days: number;
  steps: TrackStep[];
}

/** How much of the track the window takes; the rest is a running extension. */
const WINDOW_SHARE = 0.85;

/** Where a day sits along the track, as a percentage: the window is linear, an extension beyond it is its own zone. */
export function trackPct(day: number, spec: TrackSpec): number {
  const d = Number.isFinite(day) ? Math.max(0, day) : 0;
  const w = Math.max(1, spec.window_days);
  if (d <= w) return (d / w) * WINDOW_SHARE * 100;
  const ext = Math.max(1, spec.extension_days);
  return (WINDOW_SHARE + Math.min(1, (d - w) / ext) * (1 - WINDOW_SHARE)) * 100;
}

export interface TrackMark { day: number; label: string; kind: '' | 'rescue' | 'close' | 'end' }

/** The labelled marks: signup, the rescue day, each row's first day (one label a day), the window's end. */
export function trackMarks(spec: TrackSpec): TrackMark[] {
  const marks = new Map<number, TrackMark>([[0, { day: 0, label: 'day 0', kind: '' }]]);
  marks.set(spec.rescue_from_day, { day: spec.rescue_from_day, label: `rescue`, kind: 'rescue' });
  for (const s of spec.steps) {
    if (s.day_from == null || s.day_from > spec.window_days) continue;
    const prev = marks.get(s.day_from);
    const label = prev && prev.day !== 0 ? `${prev.label}·T${s.template_id}` : `T${s.template_id}`;
    marks.set(s.day_from, { day: s.day_from, label, kind: s.closes ? 'close' : prev?.kind ?? '' });
  }
  marks.set(spec.window_days, { day: spec.window_days, label: `day ${spec.window_days}`, kind: 'end' });
  return [...marks.values()].sort((a, b) => a.day - b.day);
}
